import type { ContentPack } from '../engine';

const KEY = 'ajae.known.v1';

export type KnownMap = Readonly<Record<string, 0 | 1>>;

export function readKnown(): KnownMap | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return null;
    return JSON.parse(raw) as KnownMap;
  } catch {
    return null;
  }
}

function writeKnown(m: KnownMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* 저장 실패는 격차 판정 표본에서만 빠진다 */
  }
}

/** 사전 자가표기 1 안다 / 0 모른다 / -1 미표기. */
export function knownOf(m: KnownMap | null, questionId: string): 1 | 0 | -1 {
  if (m === null) return -1;
  const v = m[questionId];
  return v === 1 ? 1 : v === 0 ? 0 : -1;
}

/**
 * 첫 세션 최초 1회. 질문문만 보여주고 안다/모른다를 받는다 — 정답은 절대 노출하지 않는다.
 * 사후 표기를 쓰지 않는 이유: 정답을 본 뒤 라벨을 걷으면 빨리 푼 문제일수록 '알았다'가 되어
 * 격차 조건이 통과 쪽으로 자기충족된다(SPEC §14.3).
 */
export async function askPreRun(pack: ContentPack, host: HTMLElement): Promise<KnownMap | null> {
  const existing = readKnown();
  if (existing !== null) return existing;

  return new Promise<KnownMap | null>((resolve) => {
    const picks = new Map<string, 0 | 1>();
    host.innerHTML = '';
    host.classList.add('on', 'sheet');

    const box = document.createElement('div');
    box.className = 'panel wide';

    const h = document.createElement('h1');
    h.textContent = '시작 전에 하나만';
    const p = document.createElement('p');
    p.textContent =
      '아래 문제들, 답을 이미 아는 게 있나요? 정답은 보여주지 않습니다. 건너뛰어도 됩니다.';
    box.append(h, p);

    const list = document.createElement('div');
    list.className = 'qlist';
    for (const q of pack.questions) {
      const row = document.createElement('div');
      row.className = 'qrow';
      const text = document.createElement('span');
      text.textContent = q.q;
      const yes = document.createElement('button');
      yes.textContent = '안다';
      const no = document.createElement('button');
      no.textContent = '모른다';
      const set = (v: 0 | 1): void => {
        picks.set(q.id, v);
        yes.classList.toggle('sel', v === 1);
        no.classList.toggle('sel', v === 0);
      };
      yes.addEventListener('click', () => set(1));
      no.addEventListener('click', () => set(0));
      row.append(text, yes, no);
      list.append(row);
    }
    box.append(list);

    const actions = document.createElement('div');
    actions.className = 'actions';
    const done = document.createElement('button');
    done.className = 'primary';
    done.textContent = '시작';
    const skip = document.createElement('button');
    skip.textContent = '건너뛰기';

    const close = (result: KnownMap | null): void => {
      host.classList.remove('on', 'sheet');
      host.innerHTML = '';
      resolve(result);
    };
    done.addEventListener('click', () => {
      const m: Record<string, 0 | 1> = {};
      for (const [k, v] of picks) m[k] = v;
      writeKnown(m);
      close(m);
    });
    skip.addEventListener('click', () => {
      // 스킵도 기록한다 — 안 하면 새로고침마다 화면이 다시 뜬다.
      // 빈 맵이면 모든 문제가 k=-1(미표기)이라 격차 판정에서만 빠지고 재미 판정에는 남는다.
      writeKnown({});
      close(null);
    });

    actions.append(done, skip);
    box.append(actions);
    host.append(box);
  });
}
